/**
 * CapacityDonut — circular capacity utilisation chart for trip detail overview.
 */
import styled from "styled-components";

const ACCENT = "#e3292f";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const ChartWrap = styled.div`
  position: relative;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
`;

const CenterLabel = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
`;

const PercentValue = styled.span`
  font-size: 22px;
  font-weight: 800;
  color: #111827;
  line-height: 1;
`;

const PercentCaption = styled.span`
  font-size: 12px;
  color: #64748b;
  margin-top: 2px;
`;

const Legend = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  max-width: 220px;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 13px;
  color: #374151;
`;

const LegendLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Swatch = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`;

const Callout = styled.div`
  margin-top: 8px;
  padding: 10px 14px;
  border-radius: 8px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  font-size: 13px;
  color: #475569;
  text-align: center;
  width: 100%;
  max-width: 220px;

  strong {
    color: #111827;
    font-weight: 700;
  }
`;

const CapacityDonut = ({
  percent = 0,
  usedM3 = 0,
  totalM3 = 0,
  avgPerOrder = null,
  size = 140,
  strokeWidth = 14,
}) => {
  const clamped = Math.min(Math.max(Number(percent) || 0, 0), 100);
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (clamped / 100) * circumference;
  const center = size / 2;
  const remaining = Math.max((Number(totalM3) || 0) - (Number(usedM3) || 0), 0);

  return (
    <Wrapper aria-label={`Capacity ${clamped}% used`}>
      <ChartWrap $size={size}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke="#e2e8f0"
            strokeWidth={strokeWidth}
          />
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke={ACCENT}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            transform={`rotate(-90 ${center} ${center})`}
          />
        </svg>
        <CenterLabel>
          <PercentValue>{Math.round(clamped)}%</PercentValue>
          <PercentCaption>Used</PercentCaption>
        </CenterLabel>
      </ChartWrap>

      <Legend>
        <LegendItem>
          <LegendLeft>
            <Swatch $color={ACCENT} />
            Used
          </LegendLeft>
          <span>{Number(usedM3).toFixed(1)} m³</span>
        </LegendItem>
        <LegendItem>
          <LegendLeft>
            <Swatch $color="#cbd5e1" />
            Available
          </LegendLeft>
          <span>{remaining.toFixed(1)} m³</span>
        </LegendItem>
        <LegendItem>
          <LegendLeft>
            <Swatch $color="#64748b" />
            Total
          </LegendLeft>
          <span>{Number(totalM3).toFixed(1)} m³</span>
        </LegendItem>
      </Legend>

      {avgPerOrder ? (
        <Callout>
          <strong>{avgPerOrder}</strong> Avg. per order
        </Callout>
      ) : null}
    </Wrapper>
  );
};

export default CapacityDonut;
